import { useCallback, useEffect, useRef, useState } from 'react';

import { useProfileTaskDraftCoordinator } from '../draft/ProfileTaskDraftCoordinatorProvider';
import type { ProfileTaskParentContext } from '../model/profile-task';
import type { ProfileTaskTilePayload } from './task-tile-url';

const payloadContext = (payload: ProfileTaskTilePayload): ProfileTaskParentContext => ({
    architectureId: payload.architectureId,
    parentId: payload.parentId,
    parentType: payload.parentType,
    parentVersion: payload.parentVersion,
});

const hasDifferentTaskSource = (left: ProfileTaskTilePayload, right: ProfileTaskTilePayload): boolean =>
    left.architectureId !== right.architectureId ||
    left.parentId !== right.parentId ||
    left.parentType !== right.parentType ||
    left.parentVersion !== right.parentVersion ||
    left.draft !== right.draft ||
    (left.draft !== true && right.draft !== true && left.taskVersionId !== right.taskVersionId);

export const useProfileTaskNavigationGuard = ({
    context,
    onClose,
    onRestorePayload,
    payload,
    taskId,
}: {
    readonly context: ProfileTaskParentContext;
    readonly onClose: () => void;
    readonly onRestorePayload: (payload: ProfileTaskTilePayload) => void;
    readonly payload: ProfileTaskTilePayload;
    readonly taskId: string;
}) => {
    const coordinator = useProfileTaskDraftCoordinator();
    const previousPayload = useRef(payload);
    const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
    // Payload the user navigated to while the editor still held unapplied changes. It is restored
    // once the discard is confirmed, so the swap is only deferred, never dropped.
    const [blockedPayload, setBlockedPayload] = useState<ProfileTaskTilePayload | undefined>(undefined);

    useEffect(() => {
        const previous = previousPayload.current;
        if (!hasDifferentTaskSource(previous, payload)) {
            previousPayload.current = payload;
            return;
        }
        if (!coordinator.getState().canReplaceEditor(payloadContext(previous), taskId)) {
            setBlockedPayload(payload);
            setIsDiscardDialogOpen(true);
            onRestorePayload(previous);
            return;
        }
        previousPayload.current = payload;
    }, [coordinator, onRestorePayload, payload, taskId]);

    const requestClose = useCallback(() => {
        if (coordinator.getState().canReplaceEditor(context, taskId)) {
            onClose();
            return;
        }
        setIsDiscardDialogOpen(true);
    }, [context, coordinator, onClose, taskId]);

    const cancelDiscard = useCallback(() => {
        setBlockedPayload(undefined);
        setIsDiscardDialogOpen(false);
    }, []);
    const confirmDiscard = useCallback(() => {
        coordinator.getState().discardEditor(context, taskId);
        setIsDiscardDialogOpen(false);
        if (blockedPayload === undefined) {
            onClose();
            return;
        }
        previousPayload.current = blockedPayload;
        setBlockedPayload(undefined);
        onRestorePayload(blockedPayload);
    }, [blockedPayload, context, coordinator, onClose, onRestorePayload, taskId]);

    return { cancelDiscard, confirmDiscard, isDiscardDialogOpen, requestClose };
};
